import "@/index.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import OffersPage from "./pages/OffersPage";
import RestaurantDashboard from "./pages/RestaurantDashboard";
import SupplierDashboard from "./pages/SupplierDashboard";
import CreateOrder from "./pages/CreateOrder";
import OrderDetail from "./pages/OrderDetail";
import SupplierProfile from "./pages/SupplierProfile";
import PremiumStatus from "./pages/PremiumStatus";
import AdminDisputes from "./pages/AdminDisputes";
import AdminOverview from "./pages/AdminOverview";
import LegalPage from "./pages/LegalPage";

function homeFor(user) {
  return user.is_admin ? "/admin" : user.role === "supplier" ? "/fornitore" : "/ristorante";
}

function Protected({ children, role }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) return <div className="p-10 text-center text-slate-500">Caricamento...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) {
    return <Navigate to={homeFor(user)} replace />;
  }
  if (user.is_admin && location.pathname === "/ristorante" && !new URLSearchParams(location.search).has("as")) {
    return <Navigate to="/admin" replace />;
  }
  return children;
}

function GuestOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="p-10 text-center text-slate-500">Caricamento...</div>;
  if (user) return <Navigate to={homeFor(user)} replace />;
  return children;
}

function App() {
  useEffect(() => {
    // Register service worker for PWA + Web Push
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => {});
    }
  }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <Layout>
          <Routes>
            <Route path="/" element={<GuestOnly><Landing /></GuestOnly>} />
            <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
            <Route path="/registrazione" element={<GuestOnly><Register /></GuestOnly>} />
            <Route path="/password-dimenticata" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verifica-email" element={<VerifyEmail />} />
            <Route path="/ristorante" element={<Protected role="restaurant"><RestaurantDashboard /></Protected>} />
            <Route path="/fornitore" element={<Protected role="supplier"><SupplierDashboard /></Protected>} />
            <Route path="/nuovo-ordine" element={<Protected role="restaurant"><CreateOrder /></Protected>} />
            <Route path="/offerte" element={<Protected role="restaurant"><OffersPage /></Protected>} />
            <Route path="/ordine/:id" element={<Protected><OrderDetail /></Protected>} />
            <Route path="/fornitore/profilo/:id" element={<SupplierProfile />} />
            <Route path="/premium/success" element={<Protected><PremiumStatus mode="success" /></Protected>} />
            <Route path="/premium/cancel" element={<Protected><PremiumStatus mode="cancel" /></Protected>} />
            <Route path="/admin/contestazioni" element={<Protected><AdminDisputes /></Protected>} />
            <Route path="/admin" element={<Protected><AdminOverview /></Protected>} />
            <Route path="/privacy" element={<LegalPage kind="privacy" />} />
            <Route path="/condizioni" element={<LegalPage kind="terms" />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Layout>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
